import {Storage} from "../../../Services/Storage";
import axios from "axios";
import router from "../../../router";

/**
 * Determine whether a user account is archived.
 * Supports both `archived` and `archive` flags and normalizes common value types.
 * @param {Object} user - User object from storage/API
 * @returns {boolean} true if archived, false otherwise
 */
function isArchivedUser(user) {
    const v = user && (user.archived !== undefined ? user.archived : user.archive);
    if (typeof v === 'string') {
        const s = v.toLowerCase();
        return s === 'true' || s === '1' || s === 'yes';
    }
    if (typeof v === 'number') return v === 1;
    return !!v; // handles boolean and undefined safely
}

/**
 * Normalize various truthy/falsey representations to strict booleans.
 * Accepts: true/false, "true"/"false", "1"/"0", 1/0, "yes"/"no".
 * This helper is used only on submit — forms remain unchanged per guidelines.
 * @param {*} v
 * @returns {boolean}
 */
function toBool(v) {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        return s === 'true' || s === '1' || s === 'yes';
    }
    return !!v;
}

/**
 * Ensure employee payload fields expected as booleans are booleans before API submit.
 * Do not mutate original payload; return a sanitized shallow copy.
 *
 * Note: We intentionally keep the form model unchanged and sanitize here on submit
 * to comply with: "Do not add the Boolean to the form just sanitize on submit".
 * @param {Object} payload
 * @returns {Object}
 */
function sanitizeEmployeePayload(payload) {
    const p = { ...payload };
    // Extend this list as backend evolves
    const boolFields = [
        'use2FA',
        'archived',
        'archive',
        'enabled',
        'active',
        'isSupervisor',
        'canLogin',
    ];
    boolFields.forEach((k) => {
        if (k in p) p[k] = toBool(p[k]);
    });
    // Remove empty phone_number to preserve existing value on backend
    if (p.phone_number === "" || p.phone_number === null || p.phone_number === undefined) {
        delete p.phone_number;
    }
    return p;
}

export default {
    name: 'user',
    state: {
        allUsers: [],
        attemptedLoginRoute : { name : `stock` },

        currentUser: {},
        /**
         * In-memory mirror of the active deeplink sessionId issued by
         * `POST /posAuth/{DB}/login` on the source POS app. Populated
         * by `hydrateFromDeeplinkSession` after the GET against
         * `posAuth/{DB}/session/{id}` succeeds.
         *
         * SECURITY: This is intentionally NOT persisted to localStorage /
         * Capacitor Storage. The sessionId already travels in the URL;
         * persisting it would broaden the blast radius if the local
         * store is exfiltrated. Clearing happens automatically on
         * page reload (state is in-memory only) and is also forced by
         * `clearDeeplinkSession` action on logout / store-switch.
         *
         * @type {{sessionId: ?string, storeDB: ?string}}
         */
        deeplinkSession: { sessionId: null, storeDB: null }
    },
    getters: {
        getAllUsers: (state) => state.allUsers,
        getAttemptedRoute: (state) => state.attemptedLoginRoute,
        getCurrentUser: (state) => state.currentUser,
        /**
         * Returns the active deeplink session payload, or
         * `{sessionId: null, storeDB: null}` when no deeplink
         * session is active. Consumers (e.g. `openFranchisePlatform`)
         * use this to decide whether to forward a live `sessionId`
         * to the Franchise Tomcat or to fall back to the cached
         * `mobileUser` (the normal-login path).
         * @param state
         * @returns {{sessionId: ?string, storeDB: ?string}}
         */
        getDeeplinkSession: (state) => state.deeplinkSession
    },
    mutations: {
        setAllUsers: async (state, payload) => {
            if (payload) await Storage.setAllUsers(payload)
            else payload = await Storage.getAllUsers();
            state.allUsers = payload ? payload : [];
        },
        setAttemptedRoute: (state, data) => state.attemptedLoginRoute = data,
        setSessionID:async (state , data) => {
            if (data) await Storage.setSessionID(data)
            else data = await Storage.getSessionID();
            state.current_session = data;
        },
        setCurrentUser: async (state, payload) => {
            if (payload) await Storage.setCurrentUser(payload)
            else payload = await Storage.getCurrentUser();
            state.currentUser = payload ? payload : {};
        },
        /**
         * Replace the in-memory deeplink session payload. Called by
         * `hydrateFromDeeplinkSession` after a successful
         * `posAuth/{DB}/session/{id}` GET and by `clearDeeplinkSession`
         * with `null` to wipe it (logout / store-switch).
         *
         * SECURITY NOTE: NEVER persist this to localStorage or
         * Capacitor Storage. The sessionId is bearer-grade for
         * Franchise Tomcat - keeping it RAM-only matches the existing
         * security boundary noted at the top of
         * `hydrateFromDeeplinkSession`.
         *
         * @param state
         * @param {{sessionId: ?string, storeDB: ?string} | null} payload
         */
        setDeeplinkSession: (state, payload) => {
            if (payload && typeof payload === 'object'
                && payload.sessionId && payload.storeDB) {
                state.deeplinkSession = {
                    sessionId: String(payload.sessionId),
                    storeDB: String(payload.storeDB)
                };
            } else {
                state.deeplinkSession = { sessionId: null, storeDB: null };
            }
        }
    },
    actions: {
        verifyOtp: (context, payload) => {
            // console.log("Payload:", payload);

            // Send POST request to verify OTP
            return axios
                .post(ticketApi + "support/verifyOTP", payload)
                .then(async ({ data }) => {
                    // console.log("Response Data:", data);

                    // Navigate to the route first
                    const route = { name: "stock" }; // Replace 'stock' with the desired route name
                    router.push(route);


                    // Perform the second POST request after navigation
                    const loggedUser = Storage.getItem("Supportlogged in user");

                    const storeDB = await Storage.getCurrentStoreDB();
                     const deviceID = await Storage.getDeviceID();

                    const loginPayload = {
                        username: loggedUser,
                        platform: "OnlineBackOffice",
                        pc: deviceID,
                    };

                    return axios.post(
                        `${serverApi}supervisorOverrides/${storeDB}/recordLogin`,
                        loginPayload
                    );
                })
                .catch((err) => {
                    console.error("Error during OTP verification:", err);
                    return errorHandler.tomcatError(err);
                });
        },

        // verifyOtp: (context, payload) => {
        //     console.log(payload)
        //     return axios.post(ticketApi + 'support/verifyOTP', payload)
        //         .then(({data}) => {
        //             context.commit(`setSessionID`,data)
        //             let route = context.getters.getAttemptedRoute;
        //             let routeName = route.name;
        //             const resolvedRoute = router.resolve({ name: routeName });
        //             let exists = resolvedRoute.route.name === routeName;
        //             if(!exists) route = { name : `stock` };
        //             return router.push(route);
        //         }).catch(err => errorHandler.tomcatError(err));
        // },
        getAllUsers: async (context) => {
            let storeDB = await Storage.getCurrentStoreDB();
            return axios.get(backOfficeTomcatApi + `users/${storeDB}/getAll`)
                .then(({data}) => context.commit('setAllUsers', data))
                .catch(err => errorHandler.tomcatError(err))
        },
        createEmployee: async (context, payload) => {
            let storeDB = await Storage.getCurrentStoreDB();
            const safe = sanitizeEmployeePayload(payload);
            return axios.post(franchiseTomcatApi + `employee/${storeDB}`, safe)
                .then(() => context.dispatch('getAllUsers'))
                .catch((err) => errorHandler.tomcatError(err))
        },
        createFranchiseEmployee: async (context, payload) => {
             return axios.post(backoffice + `api/franchise`, payload)
                 .catch((err) => errorHandler.tomcatError(err))
        },
        editEmployee: async (context, payload) => {
            let storeDB = await Storage.getCurrentStoreDB();
            const safe = sanitizeEmployeePayload(payload);
            return axios.put(franchiseTomcatApi + `employee/${storeDB}/${payload.id}`, safe)
                .then(() => context.dispatch(`getAllUsers`))
                .catch((err) => errorHandler.tomcatError(err))
        },
        FranchiseeditEmployee: async (context, payload) => {
            const { idn, ...newPayload } = payload; // Remove 'idn' from the payload
            return axios.put(backoffice + `api/franchise/${idn}`, newPayload)
                .catch((err) => errorHandler.tomcatError(err));
        },


        deleteEmployee: async (context, id) => {
            let storeDB = await Storage.getCurrentStoreDB();
            return axios.delete(franchiseTomcatApi + `employee/${storeDB}/${id}`)
                .then(() => context.dispatch(`getAllUsers`))
                .catch((err) => errorHandler.tomcatError(err))
        },
        deleteFranchsieEmployee: async (context, id) => {
             return axios.put(backoffice + `api/franchise/change-status/${id}?archive=true`)
                 .catch((err) => errorHandler.tomcatError(err))
        },
        /**
         * Validate user by password only (used in flows without store pre-checks).
         * Blocks archived users from logging in.
         * @param context
         * @param {string} password
         * @returns {Promise<boolean>} true if login allowed and committed; false otherwise
         */
        validateUser2: async (context, password) => {
            // Backend PIN verification.
            //
            // After the password-encryption rollout (Argon2id hashes stored
            // in emp.Password) the client can no longer compare the typed
            // PIN against the cached users list - hashes are one-way.
            // Verification has to move server-side, so we now call
            // POST {franchiseTomcatApi}/posAuth/{DB}/login which uses
            // PasswordEncryptionService.verify() under the hood.
            try {
                const storeDB = await Storage.getCurrentStoreDB();
                if (!storeDB) {
                    return router.push({ name: 'login' });
                }
                const { data: resp } = await axios.post(
                    franchiseTomcatApi + `posAuth/${storeDB}/login`,
                    { password }
                );
                if (!resp || !resp.success) return false;
                const users = await Storage.getAllUsers();
                const matched = (Array.isArray(users) ? users : []).find(
                    u => u.id === resp.empId
                    || (u.Username && resp.username
                        && String(u.Username).toLowerCase() === String(resp.username).toLowerCase())
                );
                if (matched && isArchivedUser(matched)) {
                    responses.throwErr('Your account is archived. Please contact your administrator.');
                    return false;
                }
                if (matched) {
                    await context.commit('setCurrentUser', matched);
                }
                return true;
            } catch (err) {
                return false;
            }
        },
        /**
         * Validate user by password with storeDB pre-check.
         * Blocks archived users and preserves boolean return contract.
         * @param context
         * @param {string} password
         * @returns {Promise<boolean>}
         */
        validateUser: async (context, password) => {
            const storeDB = await Storage.getCurrentStoreDB();
            if (!storeDB) return router.push({ name: 'login' });

            // Backend PIN verification (see validateUser2 above for full rationale).
            try {
                const { data: resp } = await axios.post(
                    franchiseTomcatApi + `posAuth/${storeDB}/login`,
                    { password }
                );
                if (!resp || !resp.success) return false;
                const users = await Storage.getAllUsers();
                const matched = (Array.isArray(users) ? users : []).find(
                    u => u.id === resp.empId
                    || (u.Username && resp.username
                        && String(u.Username).toLowerCase() === String(resp.username).toLowerCase())
                );
                if (matched && isArchivedUser(matched)) {
                    responses.throwErr('Your account is archived. Please contact your administrator.');
                    return false;
                }
                if (matched) {
                    await context.commit('setCurrentUser', matched);
                }
                context.dispatch('checkSmartInvoicing');
                return true;
            } catch (err) {
                return false;
            }
        },
        /**
         * Hydrate the current user from a deeplink sessionId issued by
         * `POST /auth/{DB}/session` on the BackOffice. Replaces the
         * previous `?auth=` deeplink payload - the user's plaintext
         * password is no longer carried in the URL.
         *
         * <p>Flow:
         * <ol>
         *   <li>Call `GET {backOfficeTomcatApi}auth/{storeDB}/session/{sessionId}`.</li>
         *   <li>On 200, fetch the full users list (so the local cache
         *       matches) and find the matching user by `email`.</li>
         *   <li>If matched, commit `setCurrentUser` with the full user
         *       record. If no local match, fall back to a minimal
         *       `{Username, Type: 'admin'}` payload and toast
         *       "Partial sign-in - please verify" so the user is
         *       flagged that the local store may be stale.</li>
         *   <li>On 404 / 401 / network error: toast
         *       "Link expired, please sign in" and return false so the
         *       caller can fall through to the password UI.</li>
         * </ol>
         *
         * <p>The sessionId is intentionally NOT cached in localStorage.
         * It already lives in the URL; persisting it would broaden the
         * blast radius if the local store is exfiltrated.
         *
         * <p>Authentication split (post 2026-07-01 refactor):
         * the deeplink flow no longer uses `mobileUser` as auth.
         * `openFranchisePlatform` reads `deeplinkSession.sessionId`
         * (set just before the `return` below) and forwards it to
         * Franchise Tomcat as a bearer credential. The `setMobileUser`
         * commit inside this action is now a router-guard signal
         * ONLY - it writes `{mainCustomerID, CustomerID}` so the
         * `router.beforeEach` guard (router/index.js ~line 969) can
         * run its Lytning subscription check. No fake email, no
         * empty password, no `fromDeeplink` flag is written here
         * any more - those fields are not consumed by the guard and
         * were only useful while `mobileUser` was still the
         * Franchise auth payload.
         *
         * @param context
         * @param {{sessionId: string, storeDB: string}} payload
         * @returns {Promise<{ok: boolean, user: ?Object}>}
         */
        hydrateFromDeeplinkSession: async (context, { sessionId, storeDB }) => {
            // eslint-disable-next-line no-console
            console.log('[deeplink][hydrateFromDeeplinkSession] called', {
                sessionIdPrefix: sessionId ? sessionId.slice(0, 8) + '...' : null,
                storeDB
            });
            if (!sessionId || !storeDB) {
                // eslint-disable-next-line no-console
                console.warn('[deeplink][hydrateFromDeeplinkSession] missing params', { sessionId: !!sessionId, storeDB: !!storeDB });
                return { ok: false, user: null, reason: 'missing-params' };
            }
            try {
                // Deeplink session lives on Franchise Online Server V2
                // (port 7082). The sessionId was minted at login time
                // by POST /posAuth/{DB}/login - see docs in
                // FranchiseV2/CHANGES.md 2026-06-22. The password NEVER
                // crosses the deeplink boundary; this GET is the only
                // call the receiving app makes.
                const base = (typeof franchiseTomcatApi === 'string' && franchiseTomcatApi)
                    || (typeof window !== 'undefined' && window.franchiseTomcatApi)
                    || '';
                // eslint-disable-next-line no-console
                console.log('[deeplink][hydrateFromDeeplinkSession] resolved base', { base, hasGlobal: typeof franchiseTomcatApi !== 'undefined' });
                if (!base) {
                    // eslint-disable-next-line no-console
                    console.error('[deeplink][hydrateFromDeeplinkSession] franchiseTomcatApi is undefined');
                    return { ok: false, user: null, reason: 'no-franchise-tomcat-api' };
                }
                const url = `${base}posAuth/${encodeURIComponent(storeDB)}/session/${encodeURIComponent(sessionId)}`;
                // eslint-disable-next-line no-console
                console.log('[deeplink][hydrateFromDeeplinkSession] GET', url);
                const res = await axios.get(
                    url,
                    { timeout: 8000 }
                );
                // eslint-disable-next-line no-console
                console.log('[deeplink][hydrateFromDeeplinkSession] response', { status: res.status, data: res.data });
                const hydrated = res && res.data;
                // The endpoint returns {success, username, store, deviceId,
                // sessionExpiresAt} - no password is ever returned.
                // We require `success` and `username` to consider the
                // session live; userId/email are not returned by the
                // current backend.
                if (!hydrated || !hydrated.success || !hydrated.username) {
                    // eslint-disable-next-line no-console
                    console.warn('[deeplink][hydrateFromDeeplinkSession] bad response shape', { hydrated });
                    return { ok: false, user: null, reason: 'bad-response' };
                }
                // Cold-cache case: the deeplink may arrive before the
                // user has ever picked a store in this browser. We
                // mirror the normal flow:
                //   mobileUserLogin → setAllStores → selectStore → loginToStore
                // by hitting the same store-record SQL
                // (`getStoresByGroup` JOIN) for a single storeDB, then
                // running `setAllStores([store])` and `loginToStore(store)`
                // so the rest of the app sees the same state shape it
                // would after a normal password sign-in. This populates
                // `store.CustomerID` (required by validatePCRegistration
                // and 12 other consumers).
                try {
                    const base = (typeof franchiseTomcatApi === 'string' && franchiseTomcatApi)
                        || (typeof window !== 'undefined' && window.franchiseTomcatApi)
                        || '';
                    const url = `${base}stores/getByDb?storeDB=${encodeURIComponent(storeDB)}`;
                    // eslint-disable-next-line no-console
                    console.log('[deeplink][hydrateFromDeeplinkSession] fetching full store record', { url, storeDB });
                    const storeRes = await axios.get(url, { timeout: 8000 });
                    const fullStore = storeRes && storeRes.data;
                    if (!fullStore || !fullStore.StoreDB) {
                        // eslint-disable-next-line no-console
                        console.warn('[deeplink][hydrateFromDeeplinkSession] store record missing fields', { fullStore });
                        await Storage.setCurrentStore({ StoreDB: storeDB });
                    } else {
                        // eslint-disable-next-line no-console
                        console.log('[deeplink][hydrateFromDeeplinkSession] got full store record', {
                            StoreDB: fullStore.StoreDB,
                            CustomerID: fullStore.CustomerID,
                            mainCustomerID: fullStore.mainCustomerID
                        });
                        // Mirror mobileUserLogin: write the full store
                        // list (one row) so the dropdown / allStores
                        // getters behave correctly.
                        await context.commit('setAllStores', [fullStore]);
                        // Router-guard signal only. The
                        // `router.beforeEach` guard (router/index.js
                        // ~line 969) refuses any route other than
                        // login/store-tologin if Storage.getMobileUser()
                        // returns null, and only reads
                        // mobileUser.mainCustomerID for the Lytning
                        // subscription check - so we commit JUST those
                        // two fields. Real auth for the Franchise
                        // Reports Platform goes through
                        // `deeplinkSession` (set just below), not this
                        // stub. We deliberately do NOT ship
                        // `username@deeplink.local`, an empty password,
                        // or `fromDeeplink: true` here - those fields
                        // were only used by the old openFranchisePlatform
                        // path that has now been replaced.
                        await context.commit('setMobileUser', {
                            mainCustomerID: fullStore.mainCustomerID,
                            CustomerID: fullStore.CustomerID,
                        });
                        // Mirror selectStore: set currentStore. We do
                        // NOT call loginToStore directly because that
                        // helper pushes router to `userLogin` at the
                        // end - we want our deeplink destination to
                        // win. We still want the same downstream
                        // effects (refreshState → validatePCRegistration),
                        // so we run refreshState ourselves.
                        await context.commit('setCurrentStore', fullStore);
                        await Storage.setCurrentStore(fullStore);
                        try {
                            await context.dispatch('refreshState');
                            // eslint-disable-next-line no-console
                            console.log('[deeplink][hydrateFromDeeplinkSession] ran refreshState', {
                                StoreDB: fullStore.StoreDB,
                                CustomerID: fullStore.CustomerID,
                                mainCustomerID: fullStore.mainCustomerID
                            });
                        } catch (rsErr) {
                            // eslint-disable-next-line no-console
                            console.warn('[deeplink][hydrateFromDeeplinkSession] refreshState failed (continuing)', rsErr);
                        }
                    }
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error('[deeplink][hydrateFromDeeplinkSession] failed to fetch/set store record (continuing)', {
                        message: e && e.message,
                        status: e && e.response && e.response.status,
                        data: e && e.response && e.response.data
                    });
                    try {
                        await Storage.setCurrentStore({ StoreDB: storeDB });
                    } catch (_) { /* best-effort */ }
                }
                // Make sure the local users cache reflects the store
                // the session was issued for (cold cache case).
                try {
                    await context.dispatch('getAllUsers');
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn('[deeplink][hydrateFromDeeplinkSession] getAllUsers failed (continuing)', e);
                    // best-effort: even if getAllUsers fails the
                    // session payload itself is enough to land on a
                    // page.
                }
                const users = await Storage.getAllUsers();
                // eslint-disable-next-line no-console
                console.log('[deeplink][hydrateFromDeeplinkSession] matching against users list', {
                    usersCount: Array.isArray(users) ? users.length : 0,
                    sessionUsername: hydrated.username
                });
                // Match by id (preferred), then email, then username.
                const matched = (Array.isArray(users) ? users : []).find(
                    u => u && (
                        (u.id && hydrated.userId && String(u.id) === String(hydrated.userId))
                        || (u.Email && hydrated.email
                            && String(u.Email).toLowerCase()
                                === String(hydrated.email).toLowerCase())
                        || (u.Username && hydrated.username
                            && String(u.Username).toLowerCase()
                                === String(hydrated.username).toLowerCase())
                    )
                );
                // eslint-disable-next-line no-console
                console.log('[deeplink][hydrateFromDeeplinkSession] match result', {
                    matched: matched ? { id: matched.id, Username: matched.Username, Email: matched.Email } : null
                });
                let user;
                if (matched) {
                    user = matched;
                } else {
                    // Minimal fallback built from the session payload
                    // itself. The password UI on the same page can be
                    // used to re-verify the credentials.
                    user = {
                        Username: hydrated.username,
                        Name: hydrated.username,
                        Email: hydrated.email || '',
                        Type: hydrated.type || 'admin',
                        isAdmin: !!hydrated.isAdmin,
                    };
                }
                await context.commit('setCurrentUser', user);
                // Cache the live sessionId in Vuex state (memory only,
                // NOT localStorage) so `openFranchisePlatform` can ship
                // it to Franchise Tomcat as a bearer credential when
                // the user clicks "Reports Platform" from the deeplink
                // session. Cleared on logout / switchStore via
                // `clearDeeplinkSession`.
                await context.commit('setDeeplinkSession', {
                    sessionId,
                    storeDB
                });
                // eslint-disable-next-line no-console
                console.log('[deeplink][hydrateFromDeeplinkSession] cached deeplinkSession', {
                    storeDB,
                    sessionIdPrefix: String(sessionId).slice(0, 8) + '...'
                });
                // eslint-disable-next-line no-console
                console.log('[deeplink][hydrateFromDeeplinkSession] committed currentUser', { partial: !matched });
                return { ok: true, user, partial: !matched };
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('[deeplink][hydrateFromDeeplinkSession] threw', {
                    message: e && e.message,
                    status: e && e.response && e.response.status,
                    data: e && e.response && e.response.data
                });
                return { ok: false, user: null, reason: 'lookup-failed' };
            }
        },
        /**
         * Wipe the in-memory deeplink sessionId so subsequent
         * `openFranchisePlatform` calls fall back to the normal
         * `mobileUser` auth payload (the real-login path).
         *
         * Called from `NavigationDrawer.vue`:
         *  - `userLogout()` - after the user signs out, before push
         *    to `userLogin`.
         *  - `switchStore()` - after `clearStoreState`, before push
         *    to `login`. The next login is a real one (or a new
         *    deeplink from the same source app) and must not reuse
         *    the previous sessionId.
         *
         * SECURITY: This is the only sanctioned way to clear the
         * sessionId from the store. Do not push `setDeeplinkSession`
         * directly from components - use this action so the cleanup
         * is observable in the Vue DevTools timeline.
         *
         * @param context
         * @returns {Promise<void>}
         */
        clearDeeplinkSession: async (context) => {
            // eslint-disable-next-line no-console
            console.log('[deeplink][clearDeeplinkSession] clearing');
            await context.commit('setDeeplinkSession', null);
        }

    }
}
